import React, { useState, useEffect, useMemo } from 'react';
import { FileText, DollarSign, Truck, Receipt, FileSpreadsheet, Plus, Eye, Trash2, Calculator, Users, Warehouse, Edit, Search, X, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { MonthlyInvoiceDialog } from '../components/MonthlyInvoiceDialog';
import { SendDraftsDialog } from '../components/SendDraftsDialog';
import { MonthlySOADialog } from '../components/MonthlySOADialog';
import { MonthlyPackDialog } from '../components/MonthlyPackDialog';
import { MonthlyRevenueChart } from '../components/MonthlyRevenueChart';
import { TopCustomers } from '../components/TopCustomers';
import { RatingsSummary } from '../components/RatingsSummary';
import { DriverProductivity } from '../components/DriverProductivity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: 'quotations', label: 'Quotations', icon: FileText, color: 'blue' },
  { key: 'invoices', label: 'Invoices', icon: FileSpreadsheet, color: 'green' },
  { key: 'delivery-orders', label: 'Delivery Orders', icon: Truck, color: 'orange' },
  { key: 'receipts', label: 'Receipts', icon: Receipt, color: 'purple' },
  { key: 'soa', label: 'Statements', icon: FileSpreadsheet, color: 'indigo' },
  { key: 'wh-quotations', label: 'WH Quotations', icon: Warehouse, color: 'teal' },
  { key: 'wh-invoices', label: 'WH Invoices', icon: Warehouse, color: 'emerald' },
];

export const BillingDashboard = () => {
  const isStaff = localStorage.getItem('njk_admin_role') === 'staff';
  const limitedStaff = isStaff && localStorage.getItem('njk_full_billing') === 'false';
  const visibleTabs = limitedStaff ? TABS.filter(t => ['invoices', 'delivery-orders'].includes(t.key)) : TABS;
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(limitedStaff ? 'invoices' : 'quotations');
  const [searchQuery, setSearchQuery] = useState('');
  const [cashOnly, setCashOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showMonthlyInvoice, setShowMonthlyInvoice] = useState(false);
  const [showSendDrafts, setShowSendDrafts] = useState(false);
  const [showMonthlySOA, setShowMonthlySOA] = useState(false);
  const [showMonthlyPack, setShowMonthlyPack] = useState(false);
  const [chasing, setChasing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const handleSendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reminders/weekly-digest`);
      const { total_outstanding, overdue_invoices, jobs_next_7_days, whatsapp_sent } = res.data;
      toast.success(
        `Digest sent to your email${whatsapp_sent ? ' + WhatsApp' : ''} — $${(total_outstanding || 0).toFixed(2)} outstanding, ${overdue_invoices} overdue, ${jobs_next_7_days} job(s) this week`,
        { duration: 8000 }
      );
    } catch (e) {
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleChaseOverdue = async () => {
    setChasing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/overdue-scan`);
      const { checked, sent } = res.data;
      if (sent > 0) {
        toast.success(`Chased ${sent} overdue invoice(s) — reminder emails sent (${checked} invoice(s) checked)`, { duration: 8000 });
      } else {
        toast.info(`No reminders sent — ${checked} invoice(s) checked. Either nothing is overdue, or they were already reminded in the last 3 days.`, { duration: 8000 });
      }
    } catch (e) {
      toast.error('Failed to run overdue chaser');
    } finally {
      setChasing(false);
    }
  };

  const [quotations, setQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [soas, setSOAs] = useState([]);
  const [whQuotations, setWhQuotations] = useState([]);
  const [whInvoices, setWhInvoices] = useState([]);

  useEffect(() => { fetchAllData(); }, []);
  useEffect(() => {
    const onFocus = () => fetchAllData();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const get = (url) => axios.get(url, { timeout: 20000 }).catch(() => axios.get(url, { timeout: 20000 }));
      const results = await Promise.allSettled([
        get(`${BACKEND_URL}/api/quotations-billing/`),
        get(`${BACKEND_URL}/api/invoices/`),
        get(`${BACKEND_URL}/api/delivery-orders/`),
        get(`${BACKEND_URL}/api/payment-receipts/`),
        get(`${BACKEND_URL}/api/soa/`),
        get(`${BACKEND_URL}/api/warehouse/quotations`),
        get(`${BACKEND_URL}/api/warehouse/invoices`)
      ]);
      const setters = [setQuotations, setInvoices, setDeliveryOrders, setReceipts, setSOAs, setWhQuotations, setWhInvoices];
      let failed = 0;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') setters[i](r.value.data);
        else failed += 1;
      });
      if (failed === results.length) {
        toast.error('Failed to load billing data — please refresh');
      } else if (failed > 0) {
        toast.warning('Some billing data is still loading — refresh to retry');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id, docNumber) => {
    if (!window.confirm(`Delete ${docNumber}? This cannot be undone.`)) return;
    const urlMap = {
      quotations: `${BACKEND_URL}/api/quotations-billing/${id}`,
      invoices: `${BACKEND_URL}/api/invoices/${id}`,
      'delivery-orders': `${BACKEND_URL}/api/delivery-orders/${id}`,
      receipts: `${BACKEND_URL}/api/payment-receipts/${id}`,
      soa: `${BACKEND_URL}/api/soa/${id}`,
      'wh-quotations': `${BACKEND_URL}/api/warehouse/quotations/${id}`,
      'wh-invoices': `${BACKEND_URL}/api/warehouse/invoices/${id}`,
    };
    try {
      await axios.delete(urlMap[type]);
      toast.success(`${docNumber} deleted`);
      fetchAllData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;
  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Filter function
  const filterItems = (items) => {
    return items.filter(item => {
      const name = (item.company_name || item.customer_name || '').toLowerCase();
      const doc = (item.document_number || '').toLowerCase();
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || name.includes(q) || doc.includes(q);
      const matchCash = !cashOnly || item.is_cash || doc.startsWith('cqt') || doc.startsWith('civ');

      let matchDate = true;
      if (dateFrom || dateTo) {
        const itemDate = item.created_at || item.invoice_date || item.payment_date || '';
        const d = itemDate ? new Date(itemDate).toISOString().split('T')[0] : '';
        if (dateFrom && d < dateFrom) matchDate = false;
        if (dateTo && d > dateTo) matchDate = false;
      }
      return matchSearch && matchDate && matchCash;
    });
  };

  const getActiveData = () => {
    const map = {
      quotations, invoices, 'delivery-orders': deliveryOrders,
      receipts, soa: soas, 'wh-quotations': whQuotations, 'wh-invoices': whInvoices
    };
    return filterItems(map[activeTab] || []);
  };

  const stats = useMemo(() => {
    const totalInv = invoices.length + whInvoices.length;
    const unpaid = invoices.filter(i => !i.paid).length + whInvoices.filter(i => i.status !== 'paid').length;
    const revenue = invoices.filter(i => i.paid).reduce((s, i) => s + (i.total_amount || 0), 0)
      + whInvoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0);
    return {
      totalDocs: quotations.length + invoices.length + deliveryOrders.length + receipts.length + soas.length + whQuotations.length + whInvoices.length,
      totalInvoices: totalInv,
      unpaid,
      revenue
    };
  }, [quotations, invoices, deliveryOrders, receipts, soas, whQuotations, whInvoices]);

  const clearFilters = () => { setSearchQuery(''); setDateFrom(''); setDateTo(''); setCashOnly(false); };

  // Row render helpers
  const getViewLink = (tab, item) => {
    const map = {
      quotations: `/admin/billing/quotation/${item.id}`,
      invoices: `/admin/billing/invoice/${item.id}`,
      'delivery-orders': `/admin/billing/delivery-order/${item.id}`,
      receipts: `/admin/billing/receipt/${item.id}`,
      soa: `/admin/billing/soa/${item.id}`,
      'wh-quotations': `/admin/warehouse/quotation/${item.id}`,
      'wh-invoices': `/admin/warehouse/invoice/${item.id}`,
    };
    return map[tab];
  };

  const getEditLink = (tab, item) => {
    const map = {
      quotations: `/admin/billing/quotation/${item.id}/edit`,
      invoices: `/admin/billing/invoice/${item.id}/edit`,
      'delivery-orders': `/admin/billing/delivery-order/${item.id}/edit`,
      receipts: `/admin/billing/receipt/${item.id}`,
      soa: `/admin/billing/soa/${item.id}/edit`,
      'wh-quotations': `/admin/warehouse/quotation/${item.id}/edit`,
      'wh-invoices': `/admin/warehouse/invoice/${item.id}/edit`,
    };
    return map[tab];
  };

  const getBadge = (tab, item) => {
    switch(tab) {
      case 'quotations': {
        if (item.customer_signature) return <Badge className="bg-green-100 text-green-800">✓ Signed</Badge>;
        return <Badge className={item.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{item.status || 'pending'}</Badge>;
      }
      case 'invoices': {
        if (item.status === 'draft') return <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`draft-badge-${item.document_number}`}>Draft — review &amp; edit</Badge>;
        if (item.paid) return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
        const bal = item.balance_due !== undefined && item.balance_due !== null ? item.balance_due : item.total_amount;
        if (item.due_date && bal > 0) {
          const daysOver = Math.floor((new Date() - new Date(item.due_date)) / 86400000);
          if (daysOver > 0) {
            return <Badge className="bg-red-600 text-white font-bold animate-pulse" data-testid={`overdue-badge-${item.document_number}`}>OVERDUE {daysOver}d</Badge>;
          }
        }
        return <Badge className="bg-red-100 text-red-800">Unpaid</Badge>;
      }
      case 'delivery-orders': {
        const bothSigned = !!(item.driver_signature && item.customer_signature);
        const partialSigned = !!(item.driver_signature || item.customer_signature);
        if (bothSigned) return <Badge className="bg-green-100 text-green-800">✓ Signed</Badge>;
        if (partialSigned) return <Badge className="bg-yellow-100 text-yellow-800">Partial sign</Badge>;
        return <Badge className={item.delivered ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>{item.delivered ? 'Delivered' : 'Pending'}</Badge>;
      }
      case 'receipts': return <Badge className="bg-purple-100 text-purple-800">{item.payment_method || 'N/A'}</Badge>;
      case 'soa': return <Badge className={item.balance_due > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>{item.balance_due > 0 ? 'Balance Due' : 'Settled'}</Badge>;
      case 'wh-quotations': return <Badge className={item.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>{item.status || 'pending'}</Badge>;
      case 'wh-invoices': return <Badge className={item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{item.status || 'unpaid'}</Badge>;
      default: return null;
    }
  };

  const getAmount = (tab, item) => {
    switch(tab) {
      case 'receipts': return formatCurrency(item.payment_amount);
      case 'soa': return formatCurrency(item.balance_due);
      default: return formatCurrency(item.total_amount);
    }
  };

  const getDate = (tab, item) => {
    switch(tab) {
      case 'invoices': return formatDate(item.invoice_date || item.created_at);
      case 'delivery-orders': return item.delivery_date || formatDate(item.created_at);
      case 'receipts': return item.payment_date || formatDate(item.created_at);
      case 'soa': return `${item.start_date} - ${item.end_date}`;
      default: return formatDate(item.created_at);
    }
  };

  const getIconColor = (tab) => {
    const colors = { quotations: 'text-blue-600', invoices: 'text-green-600', 'delivery-orders': 'text-orange-600', receipts: 'text-purple-600', soa: 'text-indigo-600', 'wh-quotations': 'text-teal-600', 'wh-invoices': 'text-emerald-600' };
    return colors[tab] || 'text-gray-600';
  };

  const activeTabInfo = TABS.find(t => t.key === activeTab);
  const filteredData = getActiveData();

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900" data-testid="billing-title">Billing Management</h1>
              <p className="text-gray-600 mt-1">Manage all documents: Vehicle & Warehouse</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {!limitedStaff && <Link to="/admin/billing/create-quotation"><Button className="bg-blue-600 hover:bg-blue-700 text-white" size="sm"><Plus size={16} className="mr-1" />Quotation</Button></Link>}
              {!limitedStaff && <Link to="/admin/billing/create-quotation?cash=1"><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="cash-quotation-btn"><Plus size={16} className="mr-1" />Cash Quotation</Button></Link>}
              {!limitedStaff && <Link to="/admin/billing/container-quotation"><Button className="bg-cyan-700 hover:bg-cyan-800 text-white" size="sm" data-testid="container-quote-btn"><Plus size={16} className="mr-1" />Container Quote</Button></Link>}
              <Link to="/admin/billing/create-invoice"><Button className="bg-green-600 hover:bg-green-700 text-white" size="sm"><Plus size={16} className="mr-1" />Invoice</Button></Link>
              <Link to="/admin/billing/create-invoice?cash=1"><Button className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="cash-invoice-btn"><Plus size={16} className="mr-1" />Cash Invoice</Button></Link>
              {!limitedStaff && <Button onClick={() => setShowMonthlyInvoice(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" size="sm" data-testid="open-monthly-invoice-btn"><Plus size={16} className="mr-1" />Monthly Inv</Button>}
              {!limitedStaff && <Button onClick={() => setShowSendDrafts(true)} className="bg-sky-700 hover:bg-sky-800 text-white" size="sm" data-testid="open-send-drafts-btn">Send Drafts{invoices.filter(i => i.status === 'draft').length > 0 ? ` (${invoices.filter(i => i.status === 'draft').length})` : ''}</Button>}
              <Link to="/admin/billing/create-delivery-order"><Button className="bg-orange-600 hover:bg-orange-700 text-white" size="sm"><Plus size={16} className="mr-1" />DO</Button></Link>
              {!limitedStaff && <>
              <Link to="/admin/billing/create-payment-receipt"><Button className="bg-purple-600 hover:bg-purple-700 text-white" size="sm"><Plus size={16} className="mr-1" />Receipt</Button></Link>
              <Link to="/admin/billing/create-soa"><Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm"><Plus size={16} className="mr-1" />SOA</Button></Link>
              <Button onClick={() => setShowMonthlySOA(true)} className="bg-indigo-800 hover:bg-indigo-900 text-white" size="sm" data-testid="open-monthly-soa-btn"><Plus size={16} className="mr-1" />Monthly SOA</Button>
              <Button onClick={() => setShowMonthlyPack(true)} className="bg-rose-700 hover:bg-rose-800 text-white" size="sm" data-testid="open-monthly-pack-btn"><Plus size={16} className="mr-1" />Email Pack</Button>
              <Button onClick={handleChaseOverdue} disabled={chasing} className="bg-red-600 hover:bg-red-700 text-white" size="sm" data-testid="chase-overdue-btn">{chasing ? 'Chasing...' : 'Chase Overdue Now'}</Button>
              <Button onClick={handleSendDigest} disabled={sendingDigest} className="bg-amber-600 hover:bg-amber-700 text-white" size="sm" data-testid="send-digest-btn">{sendingDigest ? 'Sending...' : 'Send Digest Now'}</Button>
              <Link to="/admin/warehouse/create-quotation"><Button className="bg-teal-600 hover:bg-teal-700 text-white" size="sm"><Plus size={16} className="mr-1" />WH Quote</Button></Link>
              </>}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Documents</p><p className="text-2xl font-bold">{stats.totalDocs}</p></div><FileText className="text-blue-600" size={28} /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">All Invoices</p><p className="text-2xl font-bold">{stats.totalInvoices}</p></div><FileSpreadsheet className="text-green-600" size={28} /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Unpaid</p><p className="text-2xl font-bold text-red-600">{stats.unpaid}</p></div><DollarSign className="text-red-600" size={28} /></div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Revenue</p><p className="text-2xl font-bold text-green-600">{formatCurrency(stats.revenue)}</p></div><Receipt className="text-green-600" size={28} /></div></CardContent></Card>
        </div>

        {/* Quick Links */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Link to="/admin/billing/calendar"><Button variant="outline" size="sm" data-testid="job-calendar-link"><FileText size={16} className="mr-1" />Job Calendar</Button></Link>
          {!isStaff && <>
          <Link to="/admin/accounting"><Button variant="outline" size="sm"><Calculator size={16} className="mr-1" />Accounting</Button></Link>
          <Link to="/admin/aging-report"><Button variant="outline" size="sm" data-testid="aging-report-link"><Calculator size={16} className="mr-1" />Aging</Button></Link>
          <Link to="/admin/customers"><Button variant="outline" size="sm"><Users size={16} className="mr-1" />Customers</Button></Link>
          <Link to="/admin/payslip"><Button variant="outline" size="sm" data-testid="payslip-link"><DollarSign size={16} className="mr-1" />Payslip</Button></Link>
          <Link to="/admin/payment-vouchers"><Button variant="outline" size="sm" data-testid="payment-vouchers-link"><Receipt size={16} className="mr-1" />Payment Vouchers</Button></Link>
          <Link to="/admin/warehouse"><Button variant="outline" size="sm"><Warehouse size={16} className="mr-1" />Warehouse</Button></Link>
          <Link to="/admin/inventory"><Button variant="outline" size="sm"><FileText size={16} className="mr-1" />Inventory</Button></Link>
          <Link to="/admin/contracts"><Button variant="outline" size="sm" data-testid="contracts-link"><FileText size={16} className="mr-1" />Contracts</Button></Link>
          </>}
        </div>

        {/* Monthly Revenue Chart + Top Customers */}
        {!limitedStaff && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><MonthlyRevenueChart /></div>
            <div><TopCustomers /><RatingsSummary /></div>
            <div className="lg:col-span-3"><DriverProductivity /></div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-2 mb-4">
          {visibleTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${activeTab === tab.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                {filterItems({
                  quotations, invoices, 'delivery-orders': deliveryOrders,
                  receipts, soa: soas, 'wh-quotations': whQuotations, 'wh-invoices': whInvoices
                }[tab.key] || []).length}
              </span>
              {tab.key === 'quotations' && quotations.filter((q) => q.is_new).length > 0 && (
                <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="quotations-new-count">
                  {quotations.filter((q) => q.is_new).length} new
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search & Filter */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search by customer name or document number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="billing-search"
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter size={16} className="text-gray-400" />
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="filter-date-from" />
                <span className="text-gray-400 text-sm">to</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-36" data-testid="filter-date-to" />
              </div>
              <Button
                variant={cashOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCashOnly(!cashOnly)}
                className={cashOnly ? 'bg-emerald-600 hover:bg-emerald-700 text-white h-9' : 'text-emerald-700 border-emerald-400 h-9'}
                data-testid="cash-only-filter"
              >
                💵 Cash only
              </Button>
              {(searchQuery || dateFrom || dateTo || cashOnly) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-gray-500">
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {activeTabInfo && <activeTabInfo.icon size={20} className={getIconColor(activeTab)} />}
              {activeTabInfo?.label} ({filteredData.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-900 rounded-full animate-spin mx-auto" />
                <p className="mt-2 text-gray-500">Loading...</p>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery || dateFrom || dateTo ? 'No results match your search.' : `No ${activeTabInfo?.label?.toLowerCase()} yet.`}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left p-3 font-semibold">Document</th>
                      <th className="text-left p-3 font-semibold">Customer</th>
                      <th className="text-left p-3 font-semibold">Date</th>
                      <th className="text-right p-3 font-semibold">Amount</th>
                      <th className="text-center p-3 font-semibold">Status</th>
                      <th className="text-right p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            {activeTabInfo && <activeTabInfo.icon size={16} className={getIconColor(activeTab)} />}
                            <span className="font-medium">{item.document_number}</span>
                            {activeTab === 'quotations' && item.is_new && (
                              <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0 animate-pulse" data-testid={`new-badge-${item.document_number}`}>NEW</Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-gray-700">{item.company_name || item.customer_name}</td>
                        <td className="p-3 text-gray-500">{getDate(activeTab, item)}</td>
                        <td className="p-3 text-right font-semibold">{getAmount(activeTab, item)}</td>
                        <td className="p-3 text-center">{getBadge(activeTab, item)}</td>
                        <td className="p-3">
                          <div className="flex gap-1 justify-end">
                            <Link to={getViewLink(activeTab, item)}>
                              <Button variant="outline" size="sm" className="h-8 w-8 p-0" title="View"><Eye size={14} /></Button>
                            </Link>
                            <Link to={getEditLink(activeTab, item)}>
                              <Button variant="outline" size="sm" className="h-8 w-8 p-0" title="Edit"><Edit size={14} /></Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              title="Delete"
                              onClick={() => handleDelete(activeTab, item.id, item.document_number)}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <MonthlyInvoiceDialog
        open={showMonthlyInvoice}
        onOpenChange={setShowMonthlyInvoice}
        deliveryOrders={deliveryOrders}
        onCreated={fetchAllData}
      />
      <SendDraftsDialog
        open={showSendDrafts}
        onOpenChange={setShowSendDrafts}
        invoices={invoices}
        onSent={fetchAllData}
      />
      <MonthlySOADialog
        open={showMonthlySOA}
        onOpenChange={setShowMonthlySOA}
        invoices={invoices}
        whInvoices={whInvoices}
        onCreated={fetchAllData}
      />
      <MonthlyPackDialog
        open={showMonthlyPack}
        onOpenChange={setShowMonthlyPack}
        invoices={invoices}
      />
    </div>
  );
};
